import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Globe, Save, Eye, Upload, Zap, Clock, Flame, ExternalLink, Users } from 'lucide-react';

function LandingPageSetup() {
  const navigate = useNavigate();
  const { token, API, companyContext } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    // Company Info
    company_name: '',
    tagline: '',
    description: '',
    phone: '',
    email: '',
    address: '',
    website: '',
    
    // Logo
    logo_url: '',
    
    // Quote Options
    enable_instant_quote: true,
    enable_24hour_quote: true,
    enable_hearth_quote: true,
    enable_trade_account: false,
    
    // Trade Account customization
    trade_account_title: 'Trade Account',
    trade_account_description: 'Sign up for exclusive trade pricing and benefits',
    trade_account_benefits: 'Discounted trade pricing, Priority service, Dedicated account manager',
    
    // Customization
    hero_title: 'Premium Stone Worktops & Hearths',
    hero_subtitle: 'Quality craftsmanship for your kitchen and fireplace',
    instant_quote_title: 'Instant Online Quote',
    instant_quote_description: 'Get an immediate price estimate for your worktop project',
    hour24_quote_title: '24-Hour Detailed Quote',
    hour24_quote_description: 'Request a comprehensive quote - we\'ll respond within 24 hours',
    hearth_quote_title: 'Fireplace Hearth Quote',
    hearth_quote_description: 'Custom granite hearths for your fireplace',
    
    // Colors
    primary_color: '#2563eb',
    accent_color: '#7c3aed',
    
    // Social/Links
    show_social_links: false,
    facebook_url: '',
    instagram_url: '',
    
    // Footer
    footer_text: ''
  });
  
  const [companySlug, setCompanySlug] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/landing-page-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
        // Get slug from settings if available
        if (response.data.slug) {
          setCompanySlug(response.data.slug);
        }
      }
      
      // Also fetch company info to pre-fill and get slug
      try {
        const companyRes = await axios.get(`${API}/companies/me/info`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (companyRes.data) {
          setCompanySlug(companyRes.data.slug || companyRes.data.id || '');
          setSettings(prev => ({
            ...prev,
            company_name: prev.company_name || companyRes.data.name || '',
            phone: prev.phone || companyRes.data.phone || '',
            email: prev.email || companyRes.data.email || '',
            address: prev.address || companyRes.data.address || '',
            website: prev.website || companyRes.data.website || '',
            tagline: prev.tagline || companyRes.data.branding?.company_tagline || '',
            logo_url: prev.logo_url || companyRes.data.branding?.logo_url || companyRes.data.branding?.logo_data || ''
          }));
        }
      } catch (e) {
        // Fall back to companyContext if /companies/me fails
        if (companyContext) {
          setCompanySlug(companyContext.slug || companyContext.id || '');
          setSettings(prev => ({
            ...prev,
            company_name: prev.company_name || companyContext.name || '',
            phone: prev.phone || companyContext.phone || '',
            email: prev.email || companyContext.email || '',
            address: prev.address || companyContext.address || '',
            website: prev.website || companyContext.website || '',
            tagline: prev.tagline || companyContext.branding?.company_tagline || '',
            logo_url: prev.logo_url || companyContext.branding?.logo_url || companyContext.branding?.logo_data || ''
          }));
        }
      }
    } catch (error) {
      // Settings don't exist yet, try to get company info
      try {
        const companyRes = await axios.get(`${API}/companies/me/info`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (companyRes.data) {
          setCompanySlug(companyRes.data.slug || companyRes.data.id || '');
          setSettings(prev => ({
            ...prev,
            company_name: companyRes.data.name || '',
            phone: companyRes.data.phone || '',
            email: companyRes.data.email || '',
            address: companyRes.data.address || '',
            website: companyRes.data.website || '',
            tagline: companyRes.data.branding?.company_tagline || '',
            logo_url: companyRes.data.branding?.logo_url || companyRes.data.branding?.logo_data || ''
          }));
        }
      } catch (e) {
        if (companyContext) {
          setCompanySlug(companyContext.slug || companyContext.id || '');
          setSettings(prev => ({
            ...prev,
            company_name: companyContext.name || '',
            phone: companyContext.phone || '',
            email: companyContext.email || '',
            address: companyContext.address || '',
            website: companyContext.website || '',
            tagline: companyContext.branding?.company_tagline || '',
            logo_url: companyContext.branding?.logo_url || companyContext.branding?.logo_data || ''
          }));
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate required fields
    if (!settings.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (!settings.email.trim()) {
      toast.error('Email address is required');
      return;
    }
    if (!settings.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    
    // At least one quote option must be enabled
    if (!settings.enable_instant_quote && !settings.enable_24hour_quote && !settings.enable_hearth_quote) {
      toast.error('At least one quote option must be enabled');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/landing-page-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Landing page settings saved successfully!');
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to save settings'); toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setSettings(prev => ({ ...prev, logo_url: event.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const getPreviewUrl = () => {
    return `/company/${companySlug || 'preview'}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Globe className="w-8 h-8 text-blue-600" />
                Landing Page Setup
              </h1>
              <p className="text-gray-600 mt-1">Configure your public landing page for customers</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => window.open(getPreviewUrl(), '_blank')}
              disabled={!companySlug}
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </div>

        {/* Company Information */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Company Information</CardTitle>
            <CardDescription>This information will be displayed on your public landing page</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={settings.company_name}
                  onChange={(e) => setSettings(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="Your Company Name"
                />
              </div>
              <div>
                <Label>Tagline</Label>
                <Input
                  value={settings.tagline}
                  onChange={(e) => setSettings(prev => ({ ...prev, tagline: e.target.value }))}
                  placeholder="Quality Stone Worktops"
                />
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={settings.description}
                onChange={(e) => setSettings(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Tell customers about your business..."
                rows={3}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="quotes@yourcompany.com"
                />
              </div>
              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={settings.phone}
                  onChange={(e) => setSettings(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="01onal 234 567890"
                />
              </div>
            </div>

            <div>
              <Label>Address</Label>
              <Textarea
                value={settings.address}
                onChange={(e) => setSettings(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Your business address"
                rows={2}
              />
            </div>

            <div>
              <Label>Website</Label>
              <Input
                value={settings.website}
                onChange={(e) => setSettings(prev => ({ ...prev, website: e.target.value }))}
                placeholder="www.yourcompany.com"
              />
            </div>

            {/* Logo Upload */}
            <div>
              <Label>Company Logo</Label>
              <div className="mt-2 flex items-center gap-4">
                {settings.logo_url && (
                  <img 
                    src={settings.logo_url} 
                    alt="Logo preview" 
                    className="h-16 object-contain border rounded p-2 bg-white"
                  />
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label htmlFor="logo-upload">
                    <Button type="button" variant="outline" asChild>
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Logo
                      </span>
                    </Button>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 2MB</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quote Options */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Quote Options</CardTitle>
            <CardDescription>Choose which quote options to display on your landing page</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Instant Quote */}
            <div className="flex items-start justify-between p-4 border rounded-lg bg-gradient-to-r from-green-50 to-emerald-50">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Zap className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Instant Online Quote</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Customers can get immediate pricing based on your materials and settings
                  </p>
                  <div className="mt-3 space-y-2">
                    <Input
                      value={settings.instant_quote_title}
                      onChange={(e) => setSettings(prev => ({ ...prev, instant_quote_title: e.target.value }))}
                      placeholder="Button title"
                      className="text-sm"
                    />
                    <Input
                      value={settings.instant_quote_description}
                      onChange={(e) => setSettings(prev => ({ ...prev, instant_quote_description: e.target.value }))}
                      placeholder="Description"
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
              <Switch
                checked={settings.enable_instant_quote}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enable_instant_quote: checked }))}
              />
            </div>

            {/* 24 Hour Quote */}
            <div className="flex items-start justify-between p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">24-Hour Detailed Quote</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Customers request a quote and you respond within 24 hours
                  </p>
                  <div className="mt-3 space-y-2">
                    <Input
                      value={settings.hour24_quote_title}
                      onChange={(e) => setSettings(prev => ({ ...prev, hour24_quote_title: e.target.value }))}
                      placeholder="Button title"
                      className="text-sm"
                    />
                    <Input
                      value={settings.hour24_quote_description}
                      onChange={(e) => setSettings(prev => ({ ...prev, hour24_quote_description: e.target.value }))}
                      placeholder="Description"
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
              <Switch
                checked={settings.enable_24hour_quote}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enable_24hour_quote: checked }))}
              />
            </div>

            {/* Hearth Quote */}
            <div className="flex items-start justify-between p-4 border rounded-lg bg-gradient-to-r from-orange-50 to-amber-50">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Flame className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Fireplace Hearth Quote</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Custom granite hearths for fireplaces with various shapes
                  </p>
                  <div className="mt-3 space-y-2">
                    <Input
                      value={settings.hearth_quote_title}
                      onChange={(e) => setSettings(prev => ({ ...prev, hearth_quote_title: e.target.value }))}
                      placeholder="Button title"
                      className="text-sm"
                    />
                    <Input
                      value={settings.hearth_quote_description}
                      onChange={(e) => setSettings(prev => ({ ...prev, hearth_quote_description: e.target.value }))}
                      placeholder="Description"
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
              <Switch
                checked={settings.enable_hearth_quote}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enable_hearth_quote: checked }))}
              />
            </div>

            {/* Trade Account */}
            <div className="flex items-start justify-between p-4 border rounded-lg bg-gradient-to-r from-purple-50 to-violet-50">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Trade Account Sign-up</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Allow trade customers to apply for a trade account with special pricing
                  </p>
                  <div className="mt-3 space-y-2">
                    <Input
                      value={settings.trade_account_title}
                      onChange={(e) => setSettings(prev => ({ ...prev, trade_account_title: e.target.value }))}
                      placeholder="Card title"
                      className="text-sm"
                    />
                    <Input
                      value={settings.trade_account_description}
                      onChange={(e) => setSettings(prev => ({ ...prev, trade_account_description: e.target.value }))}
                      placeholder="Description"
                      className="text-sm"
                    />
                    <Textarea
                      value={settings.trade_account_benefits}
                      onChange={(e) => setSettings(prev => ({ ...prev, trade_account_benefits: e.target.value }))}
                      placeholder="Benefits (comma separated)"
                      className="text-sm"
                      rows={2}
                    />
                    <p className="text-xs text-gray-500">Enter benefits separated by commas (e.g., "Trade pricing, Priority service, Dedicated manager")</p>
                  </div>
                </div>
              </div>
              <Switch
                checked={settings.enable_trade_account}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enable_trade_account: checked }))}
                data-testid="enable-trade-account-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Hero Section Customization */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Hero Section</CardTitle>
            <CardDescription>Customize the main banner on your landing page</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Hero Title</Label>
              <Input
                value={settings.hero_title}
                onChange={(e) => setSettings(prev => ({ ...prev, hero_title: e.target.value }))}
                placeholder="Premium Stone Worktops & Hearths"
              />
            </div>
            <div>
              <Label>Hero Subtitle</Label>
              <Input
                value={settings.hero_subtitle}
                onChange={(e) => setSettings(prev => ({ ...prev, hero_subtitle: e.target.value }))}
                placeholder="Quality craftsmanship for your kitchen and fireplace"
              />
            </div>
          </CardContent>
        </Card>

        {/* Your Public URL */}
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-blue-600" />
              Your Public Landing Page
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-3">
              Share this URL with your customers to let them request quotes:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 p-3 bg-white rounded border font-mono text-sm">
                {window.location.origin}/company/{companySlug || 'your-company-slug'}
              </code>
              <Button 
                variant="outline"
                disabled={!companySlug}
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/company/${companySlug}`);
                  toast.success('URL copied to clipboard!');
                }}
              >
                Copy
              </Button>
            </div>
            <div className="mt-4 flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => window.open(`/company/${companySlug}`, '_blank')}
                disabled={!companySlug}
              >
                <Eye className="w-4 h-4 mr-2" />
                View Landing Page
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.open(`/quote/${companySlug}`, '_blank')}
                disabled={!companySlug}
              >
                View Worktop Quote Page
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.open(`/hearth-quote/${companySlug}`, '_blank')}
                disabled={!companySlug}
              >
                View Hearth Quote Page
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} size="lg">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Landing Page Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default LandingPageSetup;
